import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Upload } from "lucide-react";
import { api, formatApiError, gbp } from "../lib/api";
import Triskelion from "../components/Triskelion";
import AdminDiscounts from "./AdminDiscounts";
import AdminSettings from "./AdminSettings";

const TABS = ["Products", "Import CSV", "Orders", "Enquiries", "News", "Discounts", "Settings"];
const CATEGORY_SLUGS = ["vouchers", "jewellery", "lab-grown", "celtic", "mens"];
const field = "w-full bg-ink border border-white/10 focus:border-gold outline-none px-3 py-2.5 text-sm text-ivory placeholder:text-stone-mist/50 transition-colors";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("cg_admin_token", data.token);
      onLogin(data.user);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm border border-white/10 bg-ink-2/60 p-8">
        <Triskelion className="w-10 h-10 text-gold mx-auto mb-6" strokeWidth={8} />
        <h1 className="font-serif text-2xl text-ivory text-center font-light mb-8">Staff Sign In</h1>
        <div className="space-y-4">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={field} data-testid="admin-email" />
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={field} data-testid="admin-password" />
        </div>
        {error && <p className="text-red-400 text-xs mt-4" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-full mt-6 bg-gold text-ink py-3.5 text-xs uppercase tracking-[0.25em] font-medium hover:bg-gold-glow transition-colors disabled:opacity-50">
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
    </main>
  );
}

const EMPTY_PRODUCT = { name: "", price: "", description: "", categories: "", product_type: "", metal: "", sizes: "", collections: "", image: "", sku: "", stock_status: "in_stock", featured: false };

function ProductForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      categories: form.categories.split(/[;,]/).map((s) => s.trim()).filter(Boolean),
      sizes: form.sizes.split(/[;,]/).map((s) => s.trim()).filter(Boolean),
      collections: form.collections.split(/[;,]/).map((s) => s.trim()).filter(Boolean),
      images: form.image ? [form.image] : [],
    };
    try {
      await onSave(payload);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-gold/25 bg-ink-2/70 p-6 space-y-3 mb-8" data-testid="product-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input required value={form.name} onChange={set("name")} placeholder="Product name *" className={field} data-testid="pf-name" />
        <input required value={form.price} onChange={set("price")} placeholder="Price (GBP) *" type="number" step="0.01" className={field} data-testid="pf-price" />
        <input value={form.sku} onChange={set("sku")} placeholder="SKU / reference" className={field} data-testid="pf-sku" />
        <input value={form.metal} onChange={set("metal")} placeholder="Metal (e.g. 9ct Yellow Gold)" className={field} data-testid="pf-metal" />
        <input value={form.product_type} onChange={set("product_type")} placeholder="Type (ring, pendant...)" className={field} data-testid="pf-type" />
        <input value={form.categories} onChange={set("categories")} placeholder="Categories (jewellery; celtic...)" className={field} data-testid="pf-categories" />
        <input value={form.collections} onChange={set("collections")} placeholder="Collections (three-legs; celtic...)" className={field} data-testid="pf-collections" />
        <input value={form.sizes} onChange={set("sizes")} placeholder="Sizes (J; K; L...)" className={field} data-testid="pf-sizes" />
      </div>
      <input value={form.image} onChange={set("image")} placeholder="Image URL" className={field} data-testid="pf-image" />
      <textarea value={form.description} onChange={set("description")} placeholder="Description" rows={3} className={field} data-testid="pf-description" />
      <div className="flex flex-wrap items-center gap-6">
        <select value={form.stock_status} onChange={set("stock_status")} className={field + " w-auto"} data-testid="pf-stock">
          <option value="in_stock">In stock</option>
          <option value="out_of_stock">Sold out</option>
          <option value="made_to_order">Made to order</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-stone-mist">
          <input type="checkbox" checked={form.featured} onChange={set("featured")} className="accent-[#C5A059]" data-testid="pf-featured" /> Featured
        </label>
        <div className="ml-auto flex gap-3">
          <button type="button" onClick={onCancel} className="text-stone-mist text-xs uppercase tracking-[0.2em] hover:text-ivory">Cancel</button>
          <button type="submit" disabled={saving} className="bg-gold text-ink px-6 py-2.5 text-xs uppercase tracking-[0.2em] font-medium hover:bg-gold-glow transition-colors disabled:opacity-50" data-testid="pf-save">
            {saving ? "Saving..." : "Save Product"}
          </button>
        </div>
      </div>
    </form>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("Products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [news, setNews] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [csv, setCsv] = useState("");
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("cg_admin_token")) return setChecking(false);
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => localStorage.removeItem("cg_admin_token")).finally(() => setChecking(false));
  }, []);

  const loadProducts = () => api.get("/admin/products").then(({ data }) => setProducts(data.items)).catch(() => {});

  useEffect(() => {
    if (!user) return;
    if (tab === "Products") loadProducts();
    if (tab === "Orders") api.get("/admin/orders").then(({ data }) => setOrders(data.items)).catch(() => {});
    if (tab === "Enquiries") api.get("/admin/enquiries").then(({ data }) => setEnquiries(data.items)).catch(() => {});
    if (tab === "News") api.get("/news").then(({ data }) => setNews(data.items)).catch(() => {});
  }, [user, tab]);

  if (checking) return <main className="min-h-screen" />;
  if (!user) return <Login onLogin={setUser} />;

  const importCsv = async () => {
    try {
      const { data } = await api.post("/admin/products/import", { csv_text: csv });
      setImportResult(data);
      toast.success(`Imported: ${data.created} new, ${data.updated} updated`);
      loadProducts();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(reader.result);
    reader.readAsText(f);
  };

  return (
    <main className="pt-36 pb-24 min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <h1 className="font-serif text-3xl text-ivory font-light">Catalogue Manager</h1>
            <p className="text-stone-mist text-xs font-mono mt-1">{user.email}</p>
          </div>
          <button
            onClick={() => { localStorage.removeItem("cg_admin_token"); setUser(null); }}
            className="text-stone-mist text-xs uppercase tracking-[0.2em] hover:text-gold transition-colors"
            data-testid="admin-logout"
          >
            Sign out
          </button>
        </div>

        <div className="flex gap-1 border-b border-white/10 mb-8 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-5 py-3 text-xs uppercase tracking-[0.2em] whitespace-nowrap transition-colors ${
                tab === t ? "text-gold border-b-2 border-gold" : "text-stone-mist hover:text-ivory"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "Products" && (
          <div>
            <div className="flex flex-wrap gap-3 mb-6">
              <button onClick={() => { setCreating(!creating); setEditing(null); }} data-testid="product-new" className="flex items-center gap-2 bg-gold text-ink px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-medium hover:bg-gold-glow transition-colors">
                <Plus className="w-4 h-4" /> New Product
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm("Remove all sample products?")) return;
                  const { data } = await api.delete("/admin/products-samples");
                  toast.success(`Removed ${data.deleted} sample products`);
                  loadProducts();
                }}
                data-testid="remove-samples"
                className="border border-white/15 text-stone-mist px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:border-gold hover:text-gold transition-colors"
              >
                Remove Sample Products
              </button>
            </div>
            {creating && (
              <ProductForm
                initial={EMPTY_PRODUCT}
                onCancel={() => setCreating(false)}
                onSave={async (payload) => {
                  await api.post("/admin/products", payload);
                  toast.success("Product created");
                  setCreating(false);
                  loadProducts();
                }}
              />
            )}
            {editing && (
              <ProductForm
                initial={{
                  ...editing,
                  price: String(editing.price),
                  categories: editing.categories.join("; "),
                  sizes: editing.sizes.join("; "),
                  collections: editing.collections.join("; "),
                }}
                onCancel={() => setEditing(null)}
                onSave={async (payload) => {
                  await api.put(`/admin/products/${editing.id}`, payload);
                  toast.success("Product updated");
                  setEditing(null);
                  loadProducts();
                }}
              />
            )}
            <div className="border border-white/10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-stone-mist border-b border-white/10">
                    <th className="p-4">Product</th><th className="p-4">Price</th><th className="p-4">Categories</th><th className="p-4">Stock</th><th className="p-4">Featured</th><th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-b border-white/5 hover:bg-ink-2/50" data-testid={`admin-product-${p.sku || p.id.slice(0, 6)}`}>
                      <td className="p-4 text-ivory">
                        {p.name}
                        {p.is_sample && <span className="ml-2 text-[10px] font-mono text-gold/70 border border-gold/30 px-1.5 py-0.5">SAMPLE</span>}
                      </td>
                      <td className="p-4 font-mono text-gold">{gbp(p.price)}</td>
                      <td className="p-4 text-stone-mist text-xs">{p.categories.join(", ")}</td>
                      <td className="p-4 text-stone-mist text-xs">{p.stock_status.replace(/_/g, " ")}</td>
                      <td className="p-4 text-stone-mist text-xs">{p.featured ? "Yes" : "—"}</td>
                      <td className="p-4">
                        <div className="flex gap-3 justify-end">
                          <button onClick={() => { setEditing(p); setCreating(false); }} className="text-stone-mist hover:text-gold" data-testid={`edit-${p.id.slice(0, 6)}`}><Pencil className="w-4 h-4" /></button>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Delete "${p.name}"?`)) return;
                              await api.delete(`/admin/products/${p.id}`);
                              toast.success("Product deleted");
                              loadProducts();
                            }}
                            className="text-stone-mist hover:text-red-400"
                            data-testid={`delete-${p.id.slice(0, 6)}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {products.length === 0 && <p className="p-8 text-center text-stone-mist text-sm">No products yet — create one or import a CSV.</p>}
            </div>
          </div>
        )}

        {tab === "Import CSV" && (
          <div className="max-w-3xl">
            <p className="text-stone-mist text-sm font-light mb-4">
              Columns: <span className="font-mono text-xs text-gold">name, price, sku, description, categories, product_type, metal, sizes, collections, image_url, stock_status, featured</span>.
              Use semicolons for multiple values (e.g. <span className="font-mono text-xs">jewellery; celtic</span>). Rows with an existing SKU are updated, not duplicated.
            </p>
            <label className="flex items-center gap-3 border border-dashed border-gold/40 p-6 cursor-pointer hover:border-gold transition-colors mb-4" data-testid="csv-file-drop">
              <Upload className="w-5 h-5 text-gold" />
              <span className="text-sm text-stone-mist">Choose a .csv file (or paste below)</span>
              <input type="file" accept=".csv,text/csv,text/plain" onChange={onFile} className="hidden" data-testid="csv-file-input" />
            </label>
            <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={8} placeholder={"name,price,sku,categories,product_type,metal,sizes,collections,image_url,stock_status,featured,description\nThree Legs Pendant,65,TLP-01,jewellery;celtic,pendant,Silver,,three-legs;manx,https://...,in_stock,true,..."} className={field + " font-mono text-xs"} data-testid="csv-text" />
            <button onClick={importCsv} disabled={!csv.trim()} data-testid="csv-import" className="mt-4 bg-gold text-ink px-8 py-3 text-xs uppercase tracking-[0.25em] font-medium hover:bg-gold-glow transition-colors disabled:opacity-40">
              Import Products
            </button>
            {importResult && (
              <div className="mt-4 border border-white/10 p-4 text-sm text-stone-mist" data-testid="import-result">
                <p>{importResult.created} created · {importResult.updated} updated</p>
                {importResult.errors.map((e, i) => <p key={i} className="text-red-400 text-xs mt-1">{e}</p>)}
              </div>
            )}
          </div>
        )}

        {tab === "Orders" && (
          <div className="space-y-4">
            {orders.map((o) => (
              <div key={o.id} className="border border-white/10 p-5" data-testid={`admin-order-${o.order_number}`}>
                <div className="flex flex-wrap justify-between gap-3 mb-3">
                  <p className="font-mono text-gold">{o.order_number}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-mist">{o.kind} · <span className={o.status === "paid" ? "text-emerald-400" : "text-gold"}>{o.status.replace(/_/g, " ")}</span></p>
                </div>
                <p className="text-ivory text-sm">{o.customer.name} · {o.customer.email}</p>
                <p className="text-stone-mist text-xs mt-1">{o.items.map((i) => `${i.sku ? `[${i.sku}] ` : ""}${i.name}${i.size ? ` (${i.size})` : ""} ×${i.qty}${i.personalisation ? ` — ${i.personalisation}` : ""}${i.delivery_mode === "email" ? ` [email voucher → ${i.recipient_email}, code ${i.voucher_code}${i.voucher_sent ? ", sent" : ""}]` : ""}`).join(" · ")}</p>
                <p className="text-stone-mist text-xs mt-1">{o.delivery_label} · Total <span className="font-mono text-gold">{gbp(o.total)}</span></p>
              </div>
            ))}
            {orders.length === 0 && <p className="text-stone-mist text-sm text-center py-16 border border-white/5">No orders yet.</p>}
          </div>
        )}

        {tab === "Enquiries" && (
          <div className="space-y-4">
            {enquiries.map((en) => (
              <div key={en.id} className="border border-white/10 p-5" data-testid={`admin-enquiry-${en.id.slice(0, 6)}`}>
                <div className="flex flex-wrap justify-between gap-3 mb-2">
                  <p className="text-gold text-xs uppercase tracking-[0.2em]">{en.type}{en.subject ? ` · ${en.subject}` : ""}</p>
                  <p className="font-mono text-xs text-stone-mist">{new Date(en.created_at).toLocaleDateString("en-GB")}</p>
                </div>
                <p className="text-ivory text-sm">{en.name || "—"} · <a href={`mailto:${en.email}`} className="text-gold">{en.email}</a>{en.phone ? ` · ${en.phone}` : ""}</p>
                {en.message && <p className="text-stone-mist text-sm font-light mt-2">{en.message}</p>}
              </div>
            ))}
            {enquiries.length === 0 && <p className="text-stone-mist text-sm text-center py-16 border border-white/5">No enquiries yet.</p>}
          </div>
        )}

        {tab === "Discounts" && <AdminDiscounts />}

        {tab === "Settings" && <AdminSettings />}

        {tab === "News" && (
          <NewsAdmin news={news} reload={() => api.get("/news").then(({ data }) => setNews(data.items))} />
        )}
      </div>
    </main>
  );
}

function NewsAdmin({ news, reload }) {
  const [form, setForm] = useState({ title: "", published: new Date().toISOString().slice(0, 10), excerpt: "", content: "", image: "", featured: false });

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/news", form);
      toast.success("Article published");
      setForm({ title: "", published: new Date().toISOString().slice(0, 10), excerpt: "", content: "", image: "", featured: false });
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
      <form onSubmit={create} className="border border-white/10 bg-ink-2/60 p-6 space-y-3 h-fit" data-testid="news-form">
        <h2 className="font-serif text-xl text-ivory font-light mb-2">New Article</h2>
        <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title *" className={field} data-testid="news-title" />
        <input type="date" value={form.published} onChange={(e) => setForm({ ...form, published: e.target.value })} className={field} data-testid="news-date" />
        <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="Image URL" className={field} data-testid="news-image" />
        <input value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Short excerpt" className={field} data-testid="news-excerpt" />
        <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Article content" rows={6} className={field} data-testid="news-content" />
        <label className="flex items-center gap-2 text-sm text-stone-mist">
          <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#C5A059]" data-testid="news-featured" /> Feature on homepage
        </label>
        <button type="submit" data-testid="news-publish" className="bg-gold text-ink px-6 py-3 text-xs uppercase tracking-[0.25em] font-medium hover:bg-gold-glow transition-colors">Publish</button>
      </form>
      <div className="space-y-4">
        {news.map((n) => (
          <div key={n.id} className="border border-white/10 p-5 flex items-start justify-between gap-4">
            <div>
              <p className="text-ivory">{n.title}</p>
              <p className="font-mono text-xs text-stone-mist mt-1">{n.published}</p>
            </div>
            <button
              onClick={async () => {
                if (!window.confirm(`Delete "${n.title}"?`)) return;
                await api.delete(`/admin/news/${n.id}`);
                toast.success("Article deleted");
                reload();
              }}
              className="text-stone-mist hover:text-red-400"
              data-testid={`news-delete-${n.slug}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
